import { Domain } from '../enums/domain';
import { DEFAULT_LOCALE } from '../utils/pagePath';

/**
 * Public paths of the magazine URL family, per brand **and** per locale
 * (spec 10.01.02 § 2.4 Magazine prefix).
 *
 * The magazine is the one URL family that does not sit at the same path on
 * every brand, and a `MagazineArticle.slug` stores only the leaf segment, so
 * the path around it cannot be reconstructed from one constant.
 *
 * Lives beside {@link ./canonicalOrigins} for the same reason that file gives:
 * the website route, the sitemap, canonical and hreflang emission and the SEO
 * health checks must agree byte-for-byte on where an article is served.
 * `packages/cms` cannot import this package from `src/` and keeps a mirror in
 * `features/redirects/magazinePaths.ts`, guarded by `sharedMirror.test.ts`.
 */

/** The four magazine page types. Keys, not paths — the path is per brand. */
export type MagazineRoute = 'listing' | 'article' | 'author' | 'category';

/** `{slug}` placeholder used in the entries that address a single entity. */
const SLUG_PLACEHOLDER = '{slug}';

/**
 * Complete paths, **not** fragments to be concatenated: the `fr` entries carry
 * their own `/fr/` prefix and translate `artikel` to `article`, so neither
 * appending a locale nor reusing another brand's prefix produces a real URL.
 *
 * A missing key means the brand does not serve that page type in that locale.
 */
const MAGAZINE_PATHS: Readonly<
  Record<
    Domain,
    Readonly<Record<string, Readonly<Partial<Record<MagazineRoute, string>>>>>
  >
> = {
  [Domain.De]: {
    de: {
      listing: '/unternehmen/magazin/',
      article: `/unternehmen/magazin/artikel/${SLUG_PLACEHOLDER}/`,
      author: `/unternehmen/magazin/autoren/${SLUG_PLACEHOLDER}/`,
      category: `/unternehmen/magazin/kategorie/${SLUG_PLACEHOLDER}/`,
    },
  },
  [Domain.At]: {
    de: {
      listing: '/unternehmen/magazin/',
      article: `/unternehmen/magazin/artikel/${SLUG_PLACEHOLDER}/`,
      author: `/unternehmen/magazin/autoren/${SLUG_PLACEHOLDER}/`,
      category: `/unternehmen/magazin/kategorie/${SLUG_PLACEHOLDER}/`,
    },
  },
  [Domain.Ch]: {
    de: {
      listing: '/blog/',
      article: `/blog/artikel/${SLUG_PLACEHOLDER}/`,
      // Singular `autor`, where `.de` uses plural `autoren`.
      author: `/blog/autor/${SLUG_PLACEHOLDER}/`,
    },
    fr: {
      listing: '/fr/blog/',
      article: `/fr/blog/article/${SLUG_PLACEHOLDER}/`,
    },
  },
  // No magazine on the Institut brand (spec 10.01.02 § 2.4).
  [Domain.Erna]: {},
};

/**
 * Locales this brand serves any magazine URL in, in map order. Empty for a
 * brand with no magazine.
 *
 * The map itself is private, so without this nothing outside can ask *which*
 * locales a brand's magazine runs in. The website reads them from
 * `getActiveDomainLocales()`; the backend has no such accessor, and a purge
 * rule that hard-coded a locale list would silently stop purging the day a
 * brand or a locale is added to the map above.
 *
 * A locale key with no route at all yields nothing — the entry exists but the
 * brand serves no magazine URL under it.
 */
export function magazineLocalesFor(domain: Domain): string[] {
  return Object.entries(MAGAZINE_PATHS[domain])
    .filter(([, routes]) => Object.values(routes).some((path) => !!path))
    .map(([locale]) => locale);
}

/**
 * Public path of one magazine page, or `undefined` when the brand does not
 * serve that page type in that locale.
 *
 * A missing `locale` resolves to {@link DEFAULT_LOCALE} rather than to no
 * match: `MagazineArticles.locale` is nullable, and a row without one is served
 * under the default locale like any other. Treating it as "no match" would drop
 * the article from every consumer silently.
 *
 * `slug` is the bare leaf segment. It is required for `article`, `author` and
 * `category` and ignored for `listing`; a blank slug on an entity route yields
 * `undefined`, since the resulting URL would address the family, not an entry.
 */
export function magazinePath(
  domain: Domain,
  locale: string | null | undefined,
  route: MagazineRoute,
  slug?: string | null,
): string | undefined {
  const template = MAGAZINE_PATHS[domain][locale || DEFAULT_LOCALE]?.[route];
  if (!template) {
    return undefined;
  }
  if (!template.includes(SLUG_PLACEHOLDER)) {
    return template;
  }
  const trimmed = slug?.replace(/^\/+|\/+$/g, '') ?? '';
  return trimmed ? template.replace(SLUG_PLACEHOLDER, trimmed) : undefined;
}

/**
 * Every brand's magazine listing path, in every locale the map serves it,
 * deduplicated (`.de` and `.at` share `/unternehmen/magazin/`).
 *
 * One build image serves all four brands, so anything that configures the URL
 * family process-wide — the Cache-Control rules in the website's
 * `next.config.ts` — must cover every brand's root at once. Derived here rather
 * than spelled out there, so a brand or a locale added to the map above is
 * picked up automatically instead of silently shipping uncached.
 */
export const magazineListingPaths = (): string[] => [
  ...new Set(
    Object.values(MAGAZINE_PATHS).flatMap((byLocale) =>
      Object.values(byLocale).flatMap((routes) => routes.listing ?? []),
    ),
  ),
];

/**
 * The same path without its own `/{locale}/` prefix.
 *
 * The map's `fr` entries are complete URLs — that is what a canonical, an
 * hreflang alternate and a sitemap entry need. Two consumers need the other
 * form: Next.js route files sit *below* the `[locale]` segment, which has
 * already swallowed the prefix, and next-intl's `Link` re-applies it itself
 * (`localePrefix: 'as-needed'`), so an unstripped href would emit `/fr/fr/…`.
 *
 * Takes the locale explicitly rather than reading the brand's locale set, so it
 * is usable wherever only the request's own locale is known.
 */
export const magazinePathWithoutLocale = (
  path: string,
  locale: string,
): string =>
  path.startsWith(`/${locale}/`) ? path.slice(locale.length + 1) : path;

/** One resolved magazine URL: which page type it addresses, and under which locale. */
export interface MagazineRouteMatch {
  route: MagazineRoute;
  /** Leaf segment of the addressed entity; absent on `listing`. */
  slug?: string;
  /** The locale whose map entry matched — not necessarily the request locale. */
  locale: string;
}

/** One `{slug}` segment: the same grammar Strapi's `uid` field produces. */
const SLUG_SEGMENT = '[a-z0-9-]+';

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** `/unternehmen/magazin/artikel/{slug}/` → `^/unternehmen/magazin/artikel/([a-z0-9-]+)/$`. */
const templateMatcher = (template: string): RegExp =>
  new RegExp(
    `^${template
      .split(SLUG_PLACEHOLDER)
      .map(escapeRegExp)
      .join(`(${SLUG_SEGMENT})`)}$`,
  );

/** Always exactly one trailing slash, always a leading one. */
const normalisePath = (path: string): string => {
  const withLeading = path.startsWith('/') ? path : `/${path}`;
  return withLeading.endsWith('/') ? withLeading : `${withLeading}/`;
};

/** Drops a leading `/{locale}` segment when it is one of `locales`. */
const dropLocaleSegment = (
  path: string,
  locales: readonly string[],
): string => {
  const [, first, ...rest] = path.split('/');
  return first && locales.includes(first) ? `/${rest.join('/')}` : path;
};

/**
 * Inverse of {@link magazinePath}: which magazine page, if any, the given
 * pathname addresses on this brand.
 *
 * Used in both directions from one declaration (spec 10.02.05 § Technical
 * Approach), so an emitted URL cannot disagree with the URL that resolves: the
 * tag bag's route recognition, the route files' own guard and every link built
 * with `magazinePath()` read the same map.
 *
 * Matched against the raw pathname first and against the locale-stripped form
 * second, because the two callers disagree on the prefix: the proxy header
 * carries `/fr/…`, while `Header.tsx` passes next-intl's `usePathname()`, which
 * has already dropped it. The raw pass runs across **all** locales before the
 * stripped one, so `/blog/` on CH resolves as `de` (its own entry) rather than
 * as the prefix-stripped French one.
 *
 * Longest template first inside each pass, so `/blog/` (listing) cannot swallow
 * `/blog/artikel/x/`.
 */
export function magazineRouteFromPathname(
  pathname: string,
  domain: Domain,
  locales: readonly string[],
): MagazineRouteMatch | undefined {
  const path = normalisePath(pathname);
  const strippedPath = normalisePath(dropLocaleSegment(path, locales));

  const candidates: {
    route: MagazineRoute;
    locale: string;
    template: string;
  }[] = [];
  for (const locale of locales) {
    const entries = MAGAZINE_PATHS[domain][locale];
    if (!entries) {
      continue;
    }
    for (const [route, template] of Object.entries(entries)) {
      if (template) {
        candidates.push({
          route: route as MagazineRoute,
          locale,
          template,
        });
      }
    }
  }
  // Longest first, so a listing prefix never wins over an entity path.
  candidates.sort((a, b) => b.template.length - a.template.length);

  for (const stripped of [false, true]) {
    for (const candidate of candidates) {
      const template = stripped
        ? normalisePath(dropLocaleSegment(candidate.template, locales))
        : candidate.template;
      const match = templateMatcher(template).exec(
        stripped ? strippedPath : path,
      );
      if (match) {
        return {
          route: candidate.route,
          ...(match[1] ? { slug: match[1] } : {}),
          locale: candidate.locale,
        };
      }
    }
  }
  return undefined;
}
